import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Paper,
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
} from '@mui/icons-material';
import {
  documentPageCanvasSx,
  documentAuthPaperSx,
  documentAuthHeadingSx,
  documentAuthFieldSx,
  documentAuthButtonSx,
  documentAuthLinkSx,
  documentAuthAlertSx,
} from '../constants/documentTheme';
import api from '../config/api';
import SEOHead from '../components/SEOHead';
import DocumentMarketingHero from '../components/DocumentMarketingHero';
import AuthPageBrandMark from '../components/AuthPageBrandMark';
import { rememberAuthNext, sanitizeNextPath } from '../utils/authNext';
import { isMemoriesHost, memoriesPath } from '../utils/memoriesHost';
import useAuthPageBranding from '../hooks/useAuthPageBranding';
import { withClientInviteQuery } from '../utils/clientInvite';
//import SocialLoginButtons from '../components/SocialLoginButtons';
const PENDING_INVITE_KEY = 'pendingTripInvite';

const Login = () => {
  const { t } = useTranslation();
  const onMemoriesHost = isMemoriesHost();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailLocked, setEmailLocked] = useState(false);
  const { login, googleCodeLogin, socialLogin, error: authError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [inviteId, setInviteId] = useState(null);
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);
  const {
    brandToken,
    branding: clientInviteBranding,
    brandName,
    brandLogoUrl,
    buttonSx,
  } = useAuthPageBranding();
  const [googleCodeHandled, setGoogleCodeHandled] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const email = searchParams.get('email');
    const locked = searchParams.get('locked');
    const invite = searchParams.get('invite');
    if (email) {
      setFormData(prev => ({ ...prev, email }));
      setEmailLocked(locked === '1');
    }
    if (invite) {
      setInviteId(invite);
      // Google sign-in returns to /login without our query string.
      sessionStorage.setItem(PENDING_INVITE_KEY, invite);
    }
  }, [location]);

  const navigateAfterLogin = useCallback(() => {
    const pendingInvite = inviteId || sessionStorage.getItem(PENDING_INVITE_KEY);
    if (pendingInvite) {
      setInviteId(pendingInvite);
      setShowAcceptDialog(true);
      return;
    }
    const next = sanitizeNextPath(new URLSearchParams(location.search).get('next'));
    const from = location.state?.from;
    if (next) {
      // Memories bridge may include ?return=https://memories… — use full navigation
      // so the query string is preserved exactly.
      if (next.includes('?')) {
        window.location.assign(next);
      } else {
        navigate(next, { replace: true });
      }
    } else if (from?.pathname) {
      navigate({ pathname: from.pathname, search: from.search || '' }, { replace: true });
    } else if (isMemoriesHost()) {
      navigate(memoriesPath(), { replace: true });
    } else {
      navigate('/trips', { replace: true });
    }
  }, [inviteId, location.search, location.state, navigate]);

  // Temporary debug: log what params arrive after Google auth redirect
  useEffect(() => {
    if (location.search) {
      console.log('[Google Auth Debug] URL:', window.location.href);
      console.log('[Google Auth Debug] Search params:', location.search);
    }
  }, [location.search]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const code = searchParams.get('code');
    const idToken = searchParams.get('id_token');
    const accessToken = searchParams.get('access_token');
    const token = searchParams.get('token');

    if (googleCodeHandled) return;
    if (!code && !idToken && !accessToken && !token) return;

    setGoogleCodeHandled(true);
    setLoading(true);
    setError('');

    if (code) {
      const redirectUri = process.env.NODE_ENV === 'production'
        ? 'https://dejavunomad.com/login'
        : `${window.location.origin}/login`;
      googleCodeLogin(code, redirectUri)
        .then((result) => {
          if (result.success) {
            navigateAfterLogin();
          } else {
            setError(result.error || 'Google sign-in failed.');
          }
        })
        .catch(() => setError('Google sign-in failed. Please try again.'))
        .finally(() => setLoading(false));
    } else {
      // Appilix native sign-in may return id_token, access_token, or token
      const googleToken = idToken || accessToken || token;
      const tokenType = accessToken ? 'accessToken' : 'idToken';
      socialLogin('google', googleToken, tokenType)
        .then((result) => {
          if (result.success) {
            navigateAfterLogin();
          } else {
            setError(result.error || 'Google sign-in failed.');
          }
        })
        .catch(() => setError('Google sign-in failed. Please try again.'))
        .finally(() => setLoading(false));
    }
  }, [location.search, googleCodeLogin, socialLogin, navigateAfterLogin, googleCodeHandled]);

  useEffect(() => {
    // Check if user was redirected due to session expiry
    if (authError && authError.includes('session has expired')) {
      setError('Your session has expired. Please log in again.');
    }
  }, [authError]);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Use unified login (handles both users and partners)
      const result = await login(formData.email, formData.password, inviteId, brandToken);
      if (result.success) {
        if (result.accountType === 'partner') {
          if (isMemoriesHost()) {
            localStorage.removeItem('partnerToken');
            localStorage.removeItem('partner');
            setError(t('memories.partnerLoginBlocked'));
          } else {
            navigate('/partner/dashboard', { replace: true });
          }
        } else {
          // Pending invitation first, otherwise the return path (e.g. /deals-plan?tab=1)
          navigateAfterLogin();
        }
      } else if (result.requiresVerification) {
        // User exists but email not verified — redirect to verify page
        const next = sanitizeNextPath(new URLSearchParams(location.search).get('next'));
        rememberAuthNext(next);
        navigate(next ? `/verify-email?next=${encodeURIComponent(next)}` : '/verify-email', {
          state: { email: result.email || formData.email, next },
          replace: true,
        });
      } else {
        setError(result.error || 'Invalid credentials');
      }
    } catch (err) {
      console.error('Login error:', err);
      if (err.response?.data?.requiresVerification) {
        const next = sanitizeNextPath(new URLSearchParams(location.search).get('next'));
        rememberAuthNext(next);
        navigate(next ? `/verify-email?next=${encodeURIComponent(next)}` : '/verify-email', {
          state: { email: err.response?.data?.email || formData.email, next },
          replace: true,
        });
      } else {
        setError(err.response?.data?.error || 'Invalid credentials');
      }
    } finally {
      setLoading(false);
    }
  };

  // Accept invitation handler
  const handleAcceptInvite = async () => {
    if (!inviteId) return;
    sessionStorage.removeItem(PENDING_INVITE_KEY);
    try {
      // Fetch invitation details to get tripId
      const res = await api.get(`/invitations/${inviteId}`);
      const tripId = res.data.trip_id;
      await api.post(`/trips/${tripId}/accept-invitation`);
      setShowAcceptDialog(false);
      navigate(`/trips/${tripId}`);
    } catch (err) {
      setError('Failed to accept invitation.');
      setShowAcceptDialog(false);
      navigate('/trips');
    }
  };

  return (
    <Box sx={documentPageCanvasSx}>
      <SEOHead
        title={brandName ? `Login - ${brandName}` : 'Login - Déjà Vu Nomad'}
        description={
          brandName
            ? `Log in to your ${brandName} account.`
            : 'Log in to your Déjà Vu Nomad account to plan trips, manage bookings, and connect with fellow travelers.'
        }
        canonical="https://dejavunomad.com/login"
        noIndex={true}
      />
      <DocumentMarketingHero
        eyebrow={t('auth.welcomeBack')}
        title={
          onMemoriesHost
            ? t('memories.signInTitle')
            : (brandName ? t('auth.signInBrandTitle', { name: brandName }) : t('auth.signInTitle'))
        }
        subtitle={
          onMemoriesHost
            ? t('memories.signInSubtitle')
            : (brandName ? t('auth.signInBrandSubtitle', { name: brandName }) : t('auth.signInSubtitle'))
        }
      />

      <Container maxWidth="sm" sx={{ py: { xs: 3, md: 4 } }}>
        <Paper elevation={0} sx={documentAuthPaperSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 4 }}>
            <AuthPageBrandMark
              brandName={brandName}
              brandLogoUrl={brandLogoUrl}
              memories={onMemoriesHost}
            />
            <Typography variant="h4" sx={documentAuthHeadingSx}>
              {t('auth.signInHeading')}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={documentAuthAlertSx}>
              {error}
            </Alert>
          )}

          {brandName && (
            <Alert severity="info" sx={documentAuthAlertSx}>
              {t('auth.joiningBrand', { name: clientInviteBranding?.name || brandName })}
            </Alert>
          )}

          {/* TODO: Re-enable Google Sign-In once mobile app auth is resolved */}
          {/* <SocialLoginButtons mode="login" /> */}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label={t('auth.emailOrUsername', 'Email or Username')}
              type="text"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              disabled={emailLocked}
              sx={{ ...documentAuthFieldSx, mt: 0 }}
            />
            
            <TextField
              required
              fullWidth
              label={t('auth.password')}
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={documentAuthFieldSx}
            />

            <Box sx={{ textAlign: 'right', mt: 1 }}>
              <Link
                component={RouterLink}
                to={withClientInviteQuery('/forgot-password', brandToken)}
                sx={documentAuthLinkSx}
              >
                {t('auth.forgotPassword')}
              </Link>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              disableElevation
              sx={buttonSx}
            >
              {loading ? (
                <CircularProgress size={22} sx={{ color: 'inherit' }} />
              ) : (
                onMemoriesHost
                  ? t('memories.signInButton')
                  : (brandName
                    ? t('auth.signInBrandButton', { name: brandName })
                    : t('auth.signInButton'))
              )}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Link 
                component={RouterLink} 
                to={(() => {
                  const next = new URLSearchParams(location.search).get('next');
                  return withClientInviteQuery(
                    '/register',
                    brandToken,
                    next && next.startsWith('/') ? { next } : {}
                  );
                })()} 
                variant="body1"
                sx={documentAuthLinkSx}
              >
                {t('auth.noAccount')}
              </Link>
            </Box>
          </Box>
        </Paper>
      </Container>
      {showAcceptDialog && (
        <Alert
          severity="info"
          sx={{ ...documentAuthAlertSx, mx: { xs: 2, sm: 'auto' }, maxWidth: 600 }}
          action={
            <Button
              variant="contained"
              disableElevation
              onClick={handleAcceptInvite}
              sx={{ ...documentAuthButtonSx, mt: 0, mb: 0, width: 'auto', minHeight: 40 }}
            >
              {t('auth.acceptInvitation')}
            </Button>
          }
        >
          {t('auth.inviteAccept')}
        </Alert>
      )}
    </Box>
  );
}

export default Login; 